import { Cursor } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { InfiniteData, useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { CALL_LIST_PAGE_OVERFLOW_ALLOWED_FIELD } from '../constants';
import { SchemaCallIntelligenceV2Service, serviceName } from '../service';
import { ListCallsIO, ServiceQueries } from '../types';
import {
  UseCallIntelligenceV2InfiniteQueryEndpointArgs,
  useCallIntelligenceV2InfiniteQuery,
} from './use-call-intelligence-v2-infinite-query';

type ListCallsPageParams = {
  lastRecordCursor?: Cursor;
};
/**
 * A hook that returns an infinite query for the `ListCalls` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListCallsQuery = <E = unknown, D = ListCallsIO['output']>({
  options,
  request,
  ...args
}: UseCallIntelligenceV2InfiniteQueryEndpointArgs<'ListCalls', E, D, ListCallsPageParams>) => {
  const pageSize = request.limit ?? 25;

  const limit = pageSize + 1; // we fetch one extra record to determine if there is a next page

  return useCallIntelligenceV2InfiniteQuery<'ListCalls', E, D, ListCallsPageParams>({
    endpointName: 'ListCalls',
    ...args,
    request: ({ pageParam }) => {
      const requestWithPageParams: ListCallsIO['input'] = {
        ...request,
        limit,
        lastRecordCursor: pageParam?.lastRecordCursor,
      };
      return requestWithPageParams;
    },
    options: {
      ...options,
      select: (response): InfiniteData<D> => {
        // Remove the extra record if it exists
        const updatedPages = response.pages.map((page) => {
          // If page overflow is allowed, we don't want to slice the newCalls array, as the UI can handle it.
          // If it's not allowed, we slice it to the page size.
          const pageOverflowAllowed = CALL_LIST_PAGE_OVERFLOW_ALLOWED_FIELD in page;
          if (!pageOverflowAllowed && page.newCalls && page.newCalls.length > pageSize) {
            return {
              ...page,
              newCalls: page.newCalls.slice(0, pageSize),
            };
          }
          return page;
        });

        const transformedData = { ...response, pages: updatedPages };
        return options?.select ? options.select(transformedData) : (transformedData as InfiniteData<D>);
      },
      getNextPageParam: (lastPage) => {
        if (!lastPage.nextCursor?.id) return undefined;

        return {
          lastRecordCursor: lastPage.nextCursor,
        };
      },
      getPreviousPageParam: (_, allPages) => {
        const pageCount = allPages.length;
        if (pageCount <= 1) return undefined;

        // previous page fetch cursor is the nextCursor of the page before
        const previousPageCursor = allPages[pageCount - 2];

        if (!previousPageCursor?.nextCursor?.id) return undefined;

        return {
          lastRecordCursor: previousPageCursor.nextCursor,
        };
      },
    },
  });
};

/**
 * A hook that returns a function to fetch the query for the `ListCalls` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchListCalls = () => {
  const queryClient = useQueryClient();

  const fetchListCalls = async <E = unknown, D = ListCallsIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCallIntelligenceV2InfiniteQueryEndpointArgs<'ListCalls', E, D>, 'options'> & {
    options: LimitedSchemaFetchQueryOptions<
      ListCallsIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListCalls'>>
    >;
  }) => {
    try {
      const response = await queryClient.fetchQuery<
        ListCallsIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListCalls'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'ListCalls'>({
          serviceName,
          endpointName: 'ListCalls',
          request,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCallIntelligenceV2Service.ListCalls(request, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError ?? ({} as D);
      }
      throw error;
    }
  };

  return fetchListCalls;
};
